/**
 * StewardWelcomeCard - Dismissable onboarding checklist shown to a new steward
 * in the Manage tab immediately after claiming an entity.
 * Dismissed state is persisted per entity in localStorage.
 * @atomic @idempotent
 */
import { memo } from 'react'
import {
  XMarkIcon,
  BoltIcon,
  Bars3BottomLeftIcon,
  CalendarDaysIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

const StewardWelcomeCard = memo(function StewardWelcomeCard({ entityName, onDismiss }) {
  const { t } = useTranslation()

  const steps = [
    { icon: BoltIcon, text: t('vendor.welcome.step1', "Use the live toggle to check in when you're open") },
    { icon: Bars3BottomLeftIcon, text: t('vendor.welcome.step2', 'Add items to your menu') },
    { icon: CalendarDaysIcon, text: t('vendor.welcome.step3', 'Set your weekly schedule') },
    { icon: MegaphoneIcon, text: t('vendor.welcome.step4', 'Send a broadcast to your followers') },
  ]

  return (
    <div className="mx-4 mt-4 mb-2 bg-primary/5 border border-primary/20 rounded-xl p-4">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {t('vendor.welcome.title', 'Welcome, steward!')}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('vendor.welcome.subtitle', "Here's how to get started")}
            {entityName ? ` — ${entityName}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          onTouchEnd={(e) => { e.preventDefault(); onDismiss() }}
          className="p-1 -mt-1 -mr-1 rounded-full hover:bg-primary/10 flex-shrink-0 transition-colors"
          aria-label={t('vendor.welcome.dismiss', 'Got it')}
        >
          <XMarkIcon className="h-4 w-4 text-gray-400 pointer-events-none" />
        </button>
      </div>

      <ul className="space-y-2">
        {steps.map(({ icon: Icon, text }, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
            <Icon className="h-4 w-4 text-primary flex-shrink-0 mt-0.5 pointer-events-none" />
            {text}
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onDismiss}
        onTouchEnd={(e) => { e.preventDefault(); onDismiss() }}
        className="w-full mt-3 py-1.5 rounded-lg text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
      >
        {t('vendor.welcome.dismiss', 'Got it')}
      </button>
    </div>
  )
})

export default StewardWelcomeCard
